import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { Alert, FormControl, FormControlLabel, MenuItem, Radio, RadioGroup, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getCompatibleCountry } from "./CompatibleCountry";
import { TenantForm } from "./useRegisterTenantButton";
import { useInputCustomerTenantDialog } from "./useInputCustomerTenantDialog";
import { ErrorDialog } from "../common/components/ErrorDialog";
import { getAuthItems } from "../auth/AuthItems";
import { CommonDialog } from "../home/CommonDialog";

interface InputCustomerTenantDialogProps {
  open: boolean;
  isEdit: boolean;
  formData: TenantForm;
  id: string;
  acceptButtonName: string;
  onConfirm: (form: TenantForm) => void;
  onCancel: () => void;
}

/**
 * お客様テナントの情報入力ダイアログのコンポーネントです。
 */
export const InputCustomerTenantDialog = (props: InputCustomerTenantDialogProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    companyNameRef,
    inputCountryRef,
    inputStateRef,
    currentCountry,
    setCurrentCountry,
    stateList,
    inputGivenNameRef,
    inputFamilyNameRef,
    inputEmailAddressRef,
    useCategory,
    setUseCategory,
    contractType,
    setContractType,
    onConfirmClick,
    errorDialogOpen,
    errorMessage,
    onAcceptError,
    duplicatedDialogOpen,
    duplicatedMessage,
    duplicatedAcceptButtonMessage,
    onAcceptDuplicated,
    onCloseDuplicated,
  } = useInputCustomerTenantDialog({
    formData: props.formData,
    id: props.id,
    onConfirm: props.onConfirm,
    open: props.open,
    isEdit: props.isEdit,
  });

  return (
    <>
      <Dialog open={props.open} onClose={() => props.onCancel()}>
        <div>
          <DialogContent>
            <Alert component="div" className="mb-5">
              {t("admin.AlertCannotUpdateCountry")}
            </Alert>
            <div className="grid grid-cols-4 gap-x-2 gap-y-5 items-center">
              <label htmlFor="inputCompanyName" className="text-base">
                {t("admin.companyName")}
              </label>
              <TextField
                className="col-span-3 w-96"
                id="inputCompanyName"
                type="text"
                defaultValue={props.formData.companyName}
                inputRef={companyNameRef}
                inputProps={{ maxLength: 100 }}
                autoFocus
              />
              <label className="text-base">{t("admin.useCategory")}</label>
              <FormControl className="col-span-3">
                <RadioGroup row>
                  <FormControlLabel
                    value="customer"
                    control={<Radio />}
                    label={t("admin.customerCategory")}
                    onChange={() => {
                      setUseCategory("customer");
                    }}
                    checked={useCategory === "customer"}
                  />
                  <FormControlLabel
                    value="demo"
                    control={<Radio />}
                    label={t("admin.demoCategory")}
                    onChange={() => {
                      setUseCategory("demo");
                    }}
                    checked={useCategory === "demo"}
                  />
                  <FormControlLabel
                    value="practice"
                    control={<Radio />}
                    label={t("admin.practiceCategory")}
                    onChange={() => {
                      setUseCategory("practice");
                    }}
                    checked={useCategory === "practice"}
                  />
                </RadioGroup>
              </FormControl>
              <label className="text-base">{t("admin.country")}</label>
              <TextField
                id="inputCountry"
                className="col-span-3"
                variant="outlined"
                defaultValue={props.formData.country}
                sx={{ margin: 0 }}
                inputRef={inputCountryRef}
                value={currentCountry}
                onChange={(e) => {
                  setCurrentCountry(e.target.value);
                }}
                select
                disabled={props.isEdit} // 変更時、国・地域情報は入力できない
              >
                {getCompatibleCountry(t, "").map((item, index) => {
                  return (
                    <MenuItem key={index} value={item.value}>
                      {item.label}
                    </MenuItem>
                  );
                })}
              </TextField>
              {getAuthItems().loginPole === "RUS" && ( // US(北米)極の場合のみ表示する
                <label className="text-base">{t("admin.state")}</label>
              )}
              {getAuthItems().loginPole === "RUS" && ( // US(北米)極の場合のみ指定する必要がある
                <TextField
                  id="inputState"
                  className="col-span-3"
                  variant="outlined"
                  defaultValue={props.formData.state}
                  sx={{ margin: 0 }}
                  inputRef={inputStateRef}
                  select
                >
                  {stateList.map((item, index) => {
                    return (
                      <MenuItem key={index} value={item.value}>
                        {item.label}
                      </MenuItem>
                    );
                  })}
                </TextField>
              )}
              <label htmlFor="inputGivenName" className="text-base">
                {t("admin.givenName")}
              </label>
              <TextField
                className="col-span-3"
                id="inputGivenName"
                inputRef={inputGivenNameRef}
                defaultValue={props.formData.givenName}
                variant="outlined"
                sx={{ margin: 0 }}
                type="text"
                inputProps={{ maxLength: 100 }}
              />
              <label htmlFor="inputFamilyName" className="text-base">
                {t("admin.familyName")}
              </label>
              <TextField
                className="col-span-3"
                id="inputFamilyName"
                inputRef={inputFamilyNameRef}
                defaultValue={props.formData.familyName}
                variant="outlined"
                sx={{ margin: 0 }}
                type="text"
                inputProps={{ maxLength: 100 }}
              />
              <label htmlFor="inputEmailAddress" className="text-base">
                {t("common.emailAddress")}
              </label>
              <TextField
                className="col-span-3"
                id="inputEmailAddress"
                inputRef={inputEmailAddressRef}
                defaultValue={props.formData.emailAddress}
                variant="outlined"
                sx={{ margin: 0 }}
                type="text"
                inputProps={{ maxLength: 80 }}
              />
              <label className="text-base">{t("admin.contractType")}</label>
              <FormControl className="col-span-3">
                <RadioGroup row>
                  <FormControlLabel
                    value="ricoh"
                    control={<Radio />}
                    label={t("admin.ricohContractType")}
                    onChange={() => {
                      setContractType("ricoh");
                    }}
                    checked={contractType === "ricoh"}
                  />
                  <FormControlLabel
                    value="distributor"
                    control={<Radio />}
                    label={t("admin.distributorContractType")}
                    onChange={() => {
                      setContractType("distributor");
                    }}
                    checked={contractType === "distributor"}
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </DialogContent>
          <DialogActions className="flex">
            <Button variant="contained" onClick={onConfirmClick}>
              {props.acceptButtonName}
            </Button>
            <Button variant="outlined" onClick={props.onCancel}>
              {t("common.cancelButton")}
            </Button>
          </DialogActions>
        </div>
      </Dialog>

      {/* エラーダイアログ */}
      <ErrorDialog open={errorDialogOpen} message={errorMessage} onAccept={onAcceptError} />

      {/* 重複の通知ダイアログ */}
      <CommonDialog
        open={duplicatedDialogOpen}
        title={""}
        onAccept={onAcceptDuplicated}
        onClose={onCloseDuplicated}
        closeButtonName={t("common.cancelButton")}
        acceptButtonName={duplicatedAcceptButtonMessage}
      >
        <div>{duplicatedMessage}</div>
      </CommonDialog>
    </>
  );
};
